import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ordersService } from '../../services/orders.service';
import { MessageService } from 'primeng/api';

export type Role = 'ROLE_KAM' | 'ROLE_PROCUREMENT' | 'ROLE_PROCUREMENT_HEAD' | 'ROLE_VENDOR';

export type CustomerRateType = 'RATE_KNOWN' | 'NULL_PRICE';

export interface OrderViewModel {
  id: number;
  orderNo: string;
  orderNumber: string;
  lrNumber: string | null;
  customer: string;
  customerName: string;
  accountId: number;
  customerId: number;
  vendorId: number;
  orderType: string;
  vehiclePd: string;
  vehicleRequiredAt: string;
  tripStatus: string;
  route: string;
  source: string;
  destination: string;
  assignedKam: string;
  assignedKamId: number;
  assignedProcExec: string;
  assignedProcurementId: number;
  numberOfVehicle: number;
  vehicleTypeId: number;
  status: string;
  biddingStatus: string;
  biddingEnable: number;
  customerRateType?: CustomerRateType;
  isNullPrice?: boolean;

  // Amounts
  customerRate?: number;
  customerAmount?: number;
  historicalRate?: number;
  finalSecondPrice?: number | null;
  finalProcCost?: number | null;
  procurementProvidedAmount?: number;
  profitAdjustedProcAmount?: number;   // adjusted proc rate (proc + 5%)
  confirmedAmount?: number;
  procHeadAmount?: number;

  // Spot - null price
  adjustedProcRate?: number;
  finalBdRate?: number | null;         // KAM enters this

  // Procurement / Proc Head shared
  procurementRate?: number | null;
  targetCeiling?: number;
  remark?: string;

  // Contract
  contractRate?: number;

  // Points
  orderPoints?: any[];
  orderStatus?: any[];

  // Raw
  sourceId?: number;
  destinationId?: number;
  routeId?: number;
}

@Component({
  selector: 'app-view-tms-order',
  templateUrl: './view-tms-order.component.html',
  styleUrls: ['./view-tms-order.component.css']
})
export class ViewTmsOrderComponent implements OnInit {
  theme = 'light';

  @Input() role: Role = 'ROLE_KAM';
  @Input() userName = '';
  order!: OrderViewModel;
  orderId: any;
  isLoading = false;
  actionLoading = false;

  // KAM — Final BD Rate input
  finalBdRateInput: number | null = null;

  // HEAD — proc head amount input
  procHeadAmountInput: number | null = null;
  selectedVendorId: number | null = null;
  vendorList: any[] = [];

  constructor(
    private router: Router,
    private activeRoute: ActivatedRoute,
    private orderService: ordersService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    const userDetails = JSON.parse(localStorage.getItem('UserDetails') || '{}');
    this.role = userDetails?.roleName || 'ROLE_KAM';
    this.userName = userDetails?.name || '';
  
    this.orderId = this.activeRoute.snapshot.params['id'];
    if (this.orderId) {
      this.getOrderDatabyId(this.orderId);
    }
  
    if (this.role === 'ROLE_PROCUREMENT_HEAD') {
      this.getVendorList();
    }
  }
  
  getVendorList(): void {
    const filters = [{ key: 'ORDER_ID', value: this.orderId.toString() }];
    this.orderService.getVendorOrders(filters, 0, 100000, 'id', 'DESC').subscribe((res: any) => {
      this.vendorList = res?.responseObject?.content || [];
    });
  }

  getOrderDatabyId(orderId: any) {
    this.isLoading = true;
    this.orderService.getOrderById(orderId).subscribe((res: any) => {
      this.isLoading = false;
      const data = res?.responseObject;
      if (data) {
        this.mapResponseToOrder(data);
      }
    }, err => {
      this.isLoading = false;
    });
  }

  mapResponseToOrder(data: any): void {
    const customerRateType: CustomerRateType = data.isNullPrice ? 'NULL_PRICE' : 'RATE_KNOWN';

    const pickup = data.orderPoints?.find((p: any) => p.pointType === 'PICKUP');
    const drop   = data.orderPoints?.find((p: any) => p.pointType === 'DROP');
    const route  = (data.source && data.destination)
      ? `${data.source} - ${data.destination}`
      : (pickup && drop)
        ? `${pickup.address} - ${drop.address}`
        : '';

    this.order = {
      id:                        data.id,
      orderNo:                   data.orderNumber,
      orderNumber:               data.orderNumber,
      lrNumber:                  data.lrNumber || null,
      customer:                  data.customerName || '',
      customerName:              data.customerName || '',
      accountId:                 data.accountId,
      customerId:                data.customerId,
      vendorId:                  data.vendorId,
      orderType:                 data.orderType || 'spot',
      vehiclePd:                 data.vehicleRequiredAt || '',
      vehicleRequiredAt:         data.vehicleRequiredAt || '',
      tripStatus:                data.tripStatus || data.status || '',
      route,
      source:                    data.source || pickup?.address || '',
      destination:               data.destination || drop?.address || '',
      assignedKam:               data.assignedKam || data.assignedKamId || '',
      assignedKamId:             data.assignedKamId,
      assignedProcExec:          data.assignedProcurement || data.assignedProcurementId || '',
      assignedProcurementId:     data.assignedProcurementId,
      numberOfVehicle:           data.numberOfVehicle || 1,
      vehicleTypeId:             data.vehicleTypeId,
      status:                    data.status || '',
      biddingStatus:             data.biddingStatus || '',
      biddingEnable:             data.biddingEnable || 0,
      customerRateType,
      isNullPrice:               data.isNullPrice,
      customerRate:              data.customerAmount,
      customerAmount:            data.customerAmount,
      historicalRate:            data.historicalRate || null,
      finalSecondPrice:          data.finalSecondPrice || null,
      finalProcCost:             data.finalConfirmedAmount || null,
      procurementProvidedAmount: data.procurementProvidedAmount,
      profitAdjustedProcAmount:  data.profitAdjustedProcAmount,
      confirmedAmount:           data.confirmedAmount,
      procHeadAmount:            data.procHeadAmount,
      procurementRate:           data.procurementProvidedAmount || null,
      adjustedProcRate:          data.profitAdjustedProcAmount ?? undefined,
      finalBdRate:               data.customerAmount || null,
      targetCeiling:             data.targetCeiling || undefined,
      remark:                    data.remarks || '',
      contractRate:              data.contractRate || undefined,
      orderPoints:               data.orderPoints || [],
      orderStatus:               data.orderStatus || [],
      sourceId:                  data.sourceId,
      destinationId:             data.destinationId,
      routeId:                   data.routeId,
    };

    // Pre-fill input fields from existing data
    // For contract use confirmedAmount, for spot use customerAmount
    this.finalBdRateInput    = data.confirmedAmount || data.customerAmount || null;
    this.procHeadAmountInput = data.procHeadAmount || null;
  }

  // ─────────────────────────────────────────────
  // STATUS HELPERS
  // ─────────────────────────────────────────────

  statusLabel(): string {
    if (!this.order?.status) return '--';
    return this.order.status
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (c: string) => c.toUpperCase());
  }

  statusBadgeClass(): string {
    const s = this.order?.status?.toUpperCase();
    if (!s) return 'badge-grey';
    if (s.includes('CONFIRMED') || s.includes('APPROVED') || s.includes('ACCEPTED')) return 'badge-green';
    if (s.includes('PENDING') || s.includes('HEAD')) return 'badge-yellow';
    if (s.includes('REJECTED')) return 'badge-red';
    if (s.includes('CREATED')) return 'badge-purple-outline';
    return 'badge-grey';
  }

  // ─────────────────────────────────────────────
  // SECTION VISIBILITY
  // ─────────────────────────────────────────────

  showPricingSection(): boolean {
    return this.role !== 'ROLE_VENDOR';
  }

  pricingSectionTitle(): string {
    if (this.role === 'ROLE_KAM') return 'Pricing';
    if (this.role === 'ROLE_PROCUREMENT') return this.isContract() ? 'Pricing' : 'Pricing — Procurement';
    if (this.role === 'ROLE_PROCUREMENT_HEAD') return 'Pricing — Full View';
    return 'Pricing';
  }

  showMarginBadge(): boolean {
    return this.role === 'ROLE_PROCUREMENT_HEAD' && this.isSpot();
  }

  isSpot(): boolean {
    return this.order?.orderType?.toLowerCase() === 'spot';
  }

  isContract(): boolean {
    return this.order?.orderType?.toLowerCase() === 'contract';
  }

  marginPercent(): string {
    if (!this.order) return '0.0';
    const sell = this.order.isNullPrice
      ? (this.finalBdRateInput || this.order.customerAmount || 0)
      : (this.order.customerAmount || 0);
    const cost = this.order.procurementProvidedAmount || 0;
    if (!sell) return '0.0';
    return (((sell - cost) / sell) * 100).toFixed(1);
  }

  marginBadgeClass(): string {
    return parseFloat(this.marginPercent()) >= 10 ? 'badge-green' : 'badge-red';
  }

  // ─────────────────────────────────────────────
  // BANNERS
  // ─────────────────────────────────────────────

  infoBannerText(): string | null {
    if (!this.order || this.isContract()) return null;
    if (!this.order.isNullPrice) {
      if (this.role === 'ROLE_KAM' || this.role === 'ROLE_PROCUREMENT') {
        return 'Customer rate is the final billing amount.';
      }
    }
    if (this.order.isNullPrice) {
      if (this.role === 'ROLE_KAM') {
        return 'Enter Final BD Rate. If less than adjusted proc rate, order goes to Head for approval.';
      }
      if (this.role === 'ROLE_PROCUREMENT') {
        return 'No customer rate. Quote freely — KAM sees +5%.';
      }
    }
    return null;
  }

  targetCeilingValue(): number | null {
    if (!this.order) return null;
    if ((this.role === 'ROLE_PROCUREMENT' || this.role === 'ROLE_PROCUREMENT_HEAD') && !this.order.isNullPrice && this.isSpot()) {
      return this.order.targetCeiling ?? null;
    }
    return null;
  }

  showApprovalRequiredBanner(): boolean {
    return this.role === 'ROLE_PROCUREMENT_HEAD' && this.order?.status === 'HEAD_PENDING';
  }

  // ─────────────────────────────────────────────
  // EDITABILITY
  // ─────────────────────────────────────────────

  canEditLrNumber(): boolean {
    return this.role === 'ROLE_KAM';
  }

  canEditProcExec(): boolean {
    return this.role === 'ROLE_KAM' || this.role === 'ROLE_PROCUREMENT';
  }

  canEditGeneralDetails(): boolean {
    return this.role === 'ROLE_KAM';
  }

  // ─────────────────────────────────────────────
  // KAM — SUBMIT FINAL BD RATE
  // amount < adjustedProcRate → HEAD_PENDING
  // amount >= adjustedProcRate → ORDER_CONFIRMED
  // ─────────────────────────────────────────────

  submitFinalBdRate(): void {
    if (!this.finalBdRateInput) {
      this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Please enter Final BD Rate' });
      return;
    }
  
    // For contract (isNullPrice false) compare against procurementProvidedAmount
    // For spot null price compare against profitAdjustedProcAmount (+5%)
    const threshold = (!this.order.isNullPrice || this.isContract())
      ? (this.order.procurementProvidedAmount || 0)
      : (this.order.adjustedProcRate || this.order.profitAdjustedProcAmount || 0);
  
    const isLess = this.finalBdRateInput < threshold;

    const payload: any = {
      id:              this.order.id,
      status:          isLess ? 'HEAD_PENDING' : 'ORDER_CONFIRMED',
      confirmedAmount: this.finalBdRateInput,
      customerAmount:  this.finalBdRateInput
    };

    this.actionLoading = true;
    this.orderService.createUpdateOrder(payload).subscribe({
      next: (res: any) => {
        this.actionLoading = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: isLess
            ? 'Order sent to Procurement Head for approval'
            : 'Order confirmed successfully'
        });
        this.getOrderDatabyId(this.orderId);
      },
      error: (err: any) => {
        this.actionLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.responseMessage || 'Something went wrong' });
      }
    });
  }

  // ─────────────────────────────────────────────
  // HEAD — APPROVE with procHeadAmount
  // ─────────────────────────────────────────────

  submitHeadApproval(): void {
    if (!this.procHeadAmountInput) {
      this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Please enter Head Approved Amount' });
      return;
    }
  
    if (!this.selectedVendorId) {
      this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Please select a vendor' });
      return;
    }
  
    const payload: any = {
      id:             this.order.id,
      status:         'HEAD_APPROVED',
      procHeadAmount: this.procHeadAmountInput,
      vendorIds:      [this.selectedVendorId]
    };

    this.actionLoading = true;
    this.orderService.createUpdateOrder(payload).subscribe({
      next: (res: any) => {
        this.actionLoading = false;
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Approved by Head' });
        this.getOrderDatabyId(this.orderId);
      },
      error: (err: any) => {
        this.actionLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.responseMessage || 'Something went wrong' });
      }
    });
  }

  // ─────────────────────────────────────────────
  // KAM — CONFIRM after HEAD_APPROVED
  // ─────────────────────────────────────────────

  confirmOrder(): void {
    const payload: any = {
      id:              this.order.id,
      status:          'ORDER_CONFIRMED',
      confirmedAmount: this.order.procHeadAmount || this.order.confirmedAmount
    };

    this.actionLoading = true;
    this.orderService.createUpdateOrder(payload).subscribe({
      next: (res: any) => {
        this.actionLoading = false;
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Order confirmed' });
        this.getOrderDatabyId(this.orderId);
      },
      error: (err: any) => {
        this.actionLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.responseMessage || 'Something went wrong' });
      }
    });
  }

  // ─────────────────────────────────────────────
  // REJECT — any role at any point
  // ─────────────────────────────────────────────

  rejectOrder(): void {
    const payload: any = {
      id:     this.order.id,
      status: 'REJECTED'
    };

    this.actionLoading = true;
    this.orderService.createUpdateOrder(payload).subscribe({
      next: (res: any) => {
        this.actionLoading = false;
        this.messageService.add({ severity: 'success', summary: 'Rejected', detail: 'Order rejected' });
        this.getOrderDatabyId(this.orderId);
      },
      error: (err: any) => {
        this.actionLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.responseMessage || 'Something went wrong' });
      }
    });
  }

  // ─────────────────────────────────────────────
  // FORMATTING & NAVIGATION
  // ─────────────────────────────────────────────

  formatCurrency(value: number | null | undefined): string {
    if (value === null || value === undefined || value === ('' as any)) return '';
    return '₹' + Number(value).toLocaleString('en-IN');
  }

  goBack(): void {
    this.router.navigate(['/orders']);
  }

  saveGeneralDetails(): void {
    console.log('Save general details');
  }
}